/**
 * Getting in: sign in, register the firm once, accept an invitation, and
 * reset a forgotten password.
 *
 * ── ONE COMPONENT, FIVE ROUTES ────────────────────────────────────────────
 * `reconcolio_dummy_ui_1_9_2026/frontend/src/pages/Auth.tsx` is the approved
 * source — five named screens, each its own `Route`. This file keeps the same
 * five names (`Login`, `Register`, `AcceptInvite`, `ForgotPassword`,
 * `ResetPassword`) but `App.tsx` points `/login`, `/register`,
 * `/accept-invite`, `/forgot-password` and `/reset-password` all at the one
 * default export below, which reads the URL and picks the view. Five route
 * table entries, one import.
 *
 * ── REGISTRATION IS NOT AN OPEN SIGN-UP ───────────────────────────────────
 * docs/user-flow.md §11: only the firm signs in, and a CA firm's tenant is not
 * something a stranger should be able to add themselves to.
 * `GET /auth/registration-open` answers true only while the installation has
 * no firm yet; `Register` checks it before showing its form, because a form
 * that only fails on submit is worse than one that says up front it is not on
 * offer. Every account after the first is created by an administrator from
 * Administration.
 *
 * ── ACCEPTING AN INVITATION, HONESTLY ─────────────────────────────────────
 * The approved screen fetched `GET /auth/invite/:token` first, to greet the
 * invitee by name and firm before they had typed anything. That lookup does
 * not exist on this API — the invite endpoints are `GET`/`POST /auth/invites`
 * (administration-only) and `POST /auth/accept-invite` (redeeming a token) —
 * there is no public read of an invite BY its token. So this screen does not
 * pretend to know who is arriving: it asks for a name and a password and lets
 * the one real endpoint be the judge, same as a wrong password gets the same
 * "that link is no longer valid" honesty as a stale one.
 *
 * ── WHERE THE TWO KINDS OF ERROR GO ───────────────────────────────────────
 * Rules the form can check itself — a missing email, a password under ten
 * characters — are zod's, and land under the input through `FormTextField`'s
 * own `fieldState.error`. Rules only the server can judge — wrong password, an
 * expired invitation — come back as an `ApiError` and go to `ErrorNotice` at
 * the top, which is given the error OBJECT rather than a message string
 * because it reads `ApiOffline` and the per-field list off it to say more
 * than the sentence alone could.
 *
 * ── FORM FIELDS NOW GO THROUGH `shared/components/form/text-field` ────────
 * A `Controller`-wrapped MUI `TextField`, not `register()` + a raw `<input>` —
 * the first screen in the app on this pattern, proving it against real
 * existing schemas (`schemas/forms.ts`) before it spreads to the rest of the
 * app in later waves.
 */
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	Link,
	useLocation,
	useNavigate,
	useSearchParams,
} from 'react-router-dom';
import { Button, CircularProgress } from '@mui/material';
import { ApiError } from '../api/errors';
import FormTextField from '../shared/components/form/text-field';
import FormErrorBox from '../shared/components/form/error';
import {
	useAcceptInviteMutation,
	useForgotPasswordMutation,
	useRegisterMutation,
	useRegistrationOpenQuery,
	useResetPasswordMutation,
	useSignInMutation,
} from '../queries/auth';
import {
	acceptInviteFormSchema,
	forgotPasswordFormSchema,
	loginFormSchema,
	registerFormSchema,
	resetPasswordFormSchema,
	type AcceptInviteForm,
	type ForgotPasswordForm,
	type LoginForm,
	type RegisterForm,
	type ResetPasswordForm,
} from '../schemas/forms';
import { ErrorNotice, Note } from '../shared/ui';

function AuthLayout({
	title,
	blurb,
	children,
	footer,
}: {
	title: string;
	blurb?: ReactNode;
	children: ReactNode;
	footer?: ReactNode;
}) {
	return (
		<div className="center-page">
			<div className="auth-card">
				<div className="auth-brand">
					<span className="mark" aria-hidden="true">
						R
					</span>
					<strong>Reconcilio</strong>
				</div>

				<div className="card" style={{ marginTop: 'var(--s5)' }}>
					<div className="card-hd">
						<div>
							<h2>{title}</h2>
							{blurb && <p className="hint">{blurb}</p>}
						</div>
					</div>
					<div className="card-bd">{children}</div>
				</div>

				{footer && (
					<p
						className="small mut"
						style={{ marginTop: 'var(--s4)', textAlign: 'center' }}
					>
						{footer}
					</p>
				)}
			</div>
		</div>
	);
}

/**
 * Put the server's field errors on the inputs they belong to.
 *
 * The API answers a rejected form with `{ fields: { email: "…" } }`, and those
 * messages read best beside the input rather than only in the notice at the
 * top. Keys the form does not have are ignored rather than dropped into a
 * `root` error, because a message about a field this screen cannot show is
 * noise to the person reading it.
 */
function applyFieldErrors<T extends Record<string, unknown>>(
	error: unknown,
	keys: readonly (keyof T & string)[],
	setError: (name: keyof T & string, error: { message: string }) => void,
): void {
	if (!(error instanceof ApiError)) return;
	for (const [field, message] of Object.entries(error.fields)) {
		if (keys.includes(field as keyof T & string)) {
			setError(field as keyof T & string, { message });
		}
	}
}

function SubmitButton({
	pending,
	pendingLabel,
	children,
}: {
	pending: boolean;
	pendingLabel: string;
	children: ReactNode;
}) {
	return (
		<Button
			type="submit"
			variant="contained"
			size="large"
			disabled={pending}
			fullWidth
		>
			{pending ? (
				<>
					<CircularProgress
						size={16}
						sx={{ marginRight: 'var(--s2)' }}
					/>
					{pendingLabel}
				</>
			) : (
				children
			)}
		</Button>
	);
}

// ═══════════════════════════════════════════════════════════════════════════

export function Login() {
	const navigate = useNavigate();
	const registration = useRegistrationOpenQuery();
	const signIn = useSignInMutation();

	const {
		control,
		handleSubmit,
		setError,
		formState: { errors },
	} = useForm<LoginForm>({
		resolver: zodResolver(loginFormSchema),
		defaultValues: { email: '', password: '' },
	});

	const submit = handleSubmit((values) => {
		signIn.mutate(values, {
			onSuccess: () => navigate('/', { replace: true }),
			onError: (error) =>
				applyFieldErrors<LoginForm>(
					error,
					['email', 'password'],
					setError,
				),
		});
	});

	return (
		<AuthLayout
			title="Sign in"
			blurb="For the audit team. The businesses you audit do not have accounts."
			footer={
				registration.data?.open ? (
					<>
						New firm? <Link to="/register">Create an account</Link>
					</>
				) : undefined
			}
		>
			<form onSubmit={submit} className="stack">
				<ErrorNotice error={signIn.error} />
				<FormTextField
					control={control}
					name="email"
					label="Email"
					isRequired
					muiProps={{
						type: 'email',
						autoComplete: 'username',
						autoFocus: true,
					}}
				/>
				<FormErrorBox errors={[errors.email]} />
				<FormTextField
					control={control}
					name="password"
					label="Password"
					isRequired
					muiProps={{
						type: 'password',
						autoComplete: 'current-password',
					}}
				/>
				<FormErrorBox errors={[errors.password]} />
				<SubmitButton
					pending={signIn.isPending}
					pendingLabel="Signing in…"
				>
					Sign in
				</SubmitButton>
				<p className="small">
					<Link to="/forgot-password">Forgotten your password?</Link>
				</p>
			</form>
		</AuthLayout>
	);
}

// ═══════════════════════════════════════════════════════════════════════════

export function Register() {
	const navigate = useNavigate();
	const registration = useRegistrationOpenQuery();
	const createFirm = useRegisterMutation();

	const {
		control,
		handleSubmit,
		setError,
		formState: { errors },
	} = useForm<RegisterForm>({
		resolver: zodResolver(registerFormSchema),
		mode: 'onChange',
		defaultValues: { firmName: '', name: '', email: '', password: '' },
	});

	const submit = handleSubmit((values) => {
		createFirm.mutate(values, {
			onSuccess: () => navigate('/', { replace: true }),
			onError: (error) =>
				applyFieldErrors<RegisterForm>(
					error,
					['firmName', 'name', 'email', 'password'],
					setError,
				),
		});
	});

	if (registration.isPending) {
		return (
			<AuthLayout title="Checking whether registration is open…">
				<span className="spinner" />
			</AuthLayout>
		);
	}

	if (!registration.data?.open) {
		return (
			<AuthLayout
				title="Registration is closed"
				footer={<Link to="/login">Back to sign in</Link>}
			>
				<Note tone="gray">
					This installation already has a firm. Every account after
					the first is created by an administrator, from
					Administration — ask yours to add you.
				</Note>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout
			title="Create your firm's account"
			blurb="You will be the administrator, and can invite the rest of your team afterwards."
			footer={
				<>
					Already have an account? <Link to="/login">Sign in</Link>
				</>
			}
		>
			<form className="stack" onSubmit={submit}>
				<ErrorNotice error={createFirm.error} />
				<FormTextField
					control={control}
					name="firmName"
					label="Firm name"
					isRequired
					muiProps={{ autoFocus: true, autoComplete: 'organization' }}
				/>
				<FormErrorBox errors={[errors.firmName]} />
				<FormTextField
					control={control}
					name="name"
					label="Your name"
					isRequired
					muiProps={{ autoComplete: 'name' }}
				/>
				<FormErrorBox errors={[errors.name]} />
				<FormTextField
					control={control}
					name="email"
					label="Email"
					isRequired
					muiProps={{ type: 'email', autoComplete: 'username' }}
				/>
				<FormErrorBox errors={[errors.email]} />
				<FormTextField
					control={control}
					name="password"
					label="Password"
					isRequired
					muiProps={{
						type: 'password',
						autoComplete: 'new-password',
						helperText:
							'At least 8 characters, with a capital letter, a small letter, a number and a special character.',
					}}
				/>
				<FormErrorBox errors={[errors.password]} />
				<SubmitButton
					pending={createFirm.isPending}
					pendingLabel="Creating…"
				>
					Create account
				</SubmitButton>
			</form>
		</AuthLayout>
	);
}

// ═══════════════════════════════════════════════════════════════════════════

export function AcceptInvite() {
	const [params] = useSearchParams();
	const token = params.get('token') ?? '';
	const navigate = useNavigate();
	const accept = useAcceptInviteMutation();

	const {
		control,
		handleSubmit,
		setError,
		formState: { errors },
	} = useForm<AcceptInviteForm>({
		resolver: zodResolver(acceptInviteFormSchema),
		mode: 'onChange',
		defaultValues: { name: '', password: '' },
	});

	const submit = handleSubmit((values) => {
		accept.mutate(
			{ token, ...values },
			{
				onSuccess: () => navigate('/', { replace: true }),
				onError: (error) =>
					applyFieldErrors<AcceptInviteForm>(
						error,
						['name', 'password'],
						setError,
					),
			},
		);
	});

	if (!token) {
		return (
			<AuthLayout title="That link is incomplete">
				<Note tone="danger">
					The invitation link has no token in it. Ask whoever invited
					you to send a fresh one.
				</Note>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout
			title="Join your firm"
			blurb="Set your name and a password to finish creating the account you were invited to."
		>
			<form className="stack" onSubmit={submit}>
				<ErrorNotice error={accept.error} />
				<FormTextField
					control={control}
					name="name"
					label="Your name"
					isRequired
					muiProps={{ autoFocus: true }}
				/>
				<FormErrorBox errors={[errors.name]} />
				<FormTextField
					control={control}
					name="password"
					label="Choose a password"
					isRequired
					muiProps={{
						type: 'password',
						autoComplete: 'new-password',
						helperText:
							'At least 8 characters, with a capital letter, a small letter, a number and a special character.',
					}}
				/>
				<FormErrorBox errors={[errors.password]} />
				<SubmitButton
					pending={accept.isPending}
					pendingLabel="Joining…"
				>
					Join the firm
				</SubmitButton>
			</form>
		</AuthLayout>
	);
}

// ═══════════════════════════════════════════════════════════════════════════

export function ForgotPassword() {
	/* View state, not form state: which of two screens is showing. It stays a
     useState — react-hook-form has nothing to say about it. */
	const [sent, setSent] = useState(false);
	const request = useForgotPasswordMutation();

	const {
		control,
		handleSubmit,
		setError,
		formState: { errors },
	} = useForm<ForgotPasswordForm>({
		resolver: zodResolver(forgotPasswordFormSchema),
		defaultValues: { email: '' },
	});

	const submit = handleSubmit((values) => {
		request.mutate(values, {
			onSuccess: () => setSent(true),
			onError: (error) =>
				applyFieldErrors<ForgotPasswordForm>(
					error,
					['email'],
					setError,
				),
		});
	});

	return (
		<AuthLayout
			title="Reset your password"
			footer={<Link to="/login">Back to sign in</Link>}
		>
			{sent ? (
				/* Deliberately says nothing about whether the address exists — a
           different message either way would turn this into a way of finding
           out who has an account (`authService.forgotPassword`'s own rule). */
				<Note tone="ok" title="If that address has an account">
					A reset link has been logged for it. Ask whoever runs this
					installation for the link if you do not receive one another
					way.
				</Note>
			) : (
				<form className="stack" onSubmit={submit}>
					<ErrorNotice error={request.error} />
					<FormTextField
						control={control}
						name="email"
						label="Email"
						isRequired
						muiProps={{ type: 'email', autoFocus: true }}
					/>
					<FormErrorBox errors={[errors.email]} />
					<SubmitButton
						pending={request.isPending}
						pendingLabel="Sending…"
					>
						Send a reset link
					</SubmitButton>
				</form>
			)}
		</AuthLayout>
	);
}

export function ResetPassword() {
	const [params] = useSearchParams();
	const token = params.get('token') ?? '';
	const navigate = useNavigate();
	const reset = useResetPasswordMutation();

	const { control, handleSubmit, setError } = useForm<ResetPasswordForm>({
		resolver: zodResolver(resetPasswordFormSchema),
		mode: 'onChange',
		defaultValues: { password: '' },
	});

	const submit = handleSubmit((values) => {
		reset.mutate(
			{ token, ...values },
			{
				onSuccess: () => navigate('/login', { replace: true }),
				onError: (error) =>
					applyFieldErrors<ResetPasswordForm>(
						error,
						['password'],
						setError,
					),
			},
		);
	});

	return (
		<AuthLayout
			title="Choose a new password"
			blurb="Setting a new password signs you out everywhere else."
			footer={<Link to="/login">Back to sign in</Link>}
		>
			{!token ? (
				<Note tone="danger">
					That reset link has no token in it. Request a new one from
					the sign-in page.
				</Note>
			) : (
				<form className="stack" onSubmit={submit}>
					<ErrorNotice error={reset.error} />
					<FormTextField
						control={control}
						name="password"
						label="New password"
						isRequired
						muiProps={{
							type: 'password',
							autoComplete: 'new-password',
							autoFocus: true,
							helperText:
								'At least 8 characters, with a capital letter, a small letter, a number and a special character.',
						}}
					/>
					<SubmitButton
						pending={reset.isPending}
						pendingLabel="Saving…"
					>
						Set password and sign in
					</SubmitButton>
				</form>
			)}
		</AuthLayout>
	);
}

// ═══════════════════════════════════════════════════════════════════════════

/** The one export `App.tsx` imports. Five routes, one component: it reads
 *  the URL it was rendered at and picks the view that belongs there. */
export default function Auth() {
	const location = useLocation();
	if (location.pathname.startsWith('/register')) return <Register />;
	if (location.pathname.startsWith('/accept-invite')) return <AcceptInvite />;
	if (location.pathname.startsWith('/forgot-password'))
		return <ForgotPassword />;
	if (location.pathname.startsWith('/reset-password'))
		return <ResetPassword />;
	return <Login />;
}
